import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@radix-ui/react-icons';

export interface StatusPageProps {
  /** Main heading displayed at the top */
  heading: string;
  /** Body content below the heading */
  description: ReactNode;
  /** Image src for the illustration */
  illustration: string;
  /** Optional back-link shown below the description */
  backLink?: { label: string; to: string };
  /** Optional error code shown at the bottom (e.g. "404") */
  errorCode?: string;
}

export function StatusPage({
  heading,
  description,
  illustration,
  backLink,
  errorCode,
}: StatusPageProps) {
  return (
    <div className="mx-auto flex min-h-[calc(100vh-186px)] max-w-[1364px] flex-col items-center gap-0 px-4 py-12 antialiased tablet:px-10 laptop:flex-row laptop:gap-8">
      {/* Left column */}
      <div className="min-w-0 flex-1 px-0 laptop:px-10">
        <h1 className="mb-6 text-4xl font-bold tablet:mb-8 tablet:text-5xl">
          {heading}
        </h1>
        <div className="space-y-4 text-xl tablet:text-2xl">{description}</div>
        {backLink && (
          <Link
            to={backLink.to}
            className="mt-10 inline-flex items-center gap-1 text-base text-blue-700 hover:underline tablet:text-lg"
          >
            <ArrowLeftIcon aria-hidden="true" className="h-5 w-5" />
            {backLink.label}
          </Link>
        )}
        <p className="mt-12 text-sm font-normal tablet:text-base">
          If you have any questions, please call customer service at
          1-800-724-2222, option 6, or email digitalservice@scholastic.com.
        </p>
        {errorCode && (
          <p className="mt-4 text-base font-normal">Error Code: {errorCode}</p>
        )}
      </div>

      {/* Right column — illustration */}
      <div className="mt-12 flex min-w-0 flex-1 justify-center laptop:mt-0">
        <img
          src={illustration}
          alt=""
          className="h-auto w-full max-w-[623px]"
        />
      </div>
    </div>
  );
}
